import { useEffect, useMemo, useRef, useState } from 'react';
import { RangeKeyDict } from 'react-date-range';

import { addYears, endOfDay, getTime, startOfDay, subYears } from 'date-fns';
import useSWR from 'swr';

import { DateRangePicker, Header, Loading, Main, Select } from 'components';
import { useAuth } from 'hooks';
import { returnRequestService } from 'services';
import { RETURN_REQUEST_STATUS_OPTIONS, ReturnRequestStatus } from 'types';
import { useReturnsRefundsFiltersStore } from 'zustand-store';

import { Pagination } from './components/pagination';
import { ReturnRequestItem } from './components/return-request-item';
import { ReturnRequestResume } from './components/return-request-resume';
import { SearchSection } from './components/search-section';

const PAST_YEARS_LIMIT = 10;
const FUTURE_YEARS_LIMIT = 1;

const MIN_DATE = subYears(new Date(), PAST_YEARS_LIMIT);
const MAX_DATE = addYears(new Date(), FUTURE_YEARS_LIMIT);

export function ReturnsAndRefundsPage() {
  const mainRef = useRef<HTMLDivElement>(null);
  const { selectedStore } = useAuth();
  const { startDate, endDate, statuses, setFilters, resetFilters } =
    useReturnsRefundsFiltersStore();

  const [currentPage, setCurrentPage] = useState(0);

  const params = {
    startDate: getTime(startDate),
    endDate: getTime(endDate),
    currentPage,
    pageSize: 5,
    statuses: statuses || '',
  };

  const { data, isLoading, mutate } = useSWR(
    [
      `return-requests-${selectedStore.brand}-${selectedStore.name}`,
      statuses,
      currentPage,
    ],
    () => returnRequestService.getReturnRequests(params)
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [statuses]);

  function changeDateRange(dateRange: RangeKeyDict) {
    const { startDate, endDate } = dateRange.range1;

    if (!startDate || !endDate) return;

    setCurrentPage(0);
    setFilters({
      startDate: startOfDay(startDate),
      endDate: endOfDay(endDate),
      statuses,
    });
  }

  function handleStatusChange(
    value:
      | string
      | React.ChangeEvent<HTMLSelectElement>
      | Event
      | HTMLInputElement
  ) {
    const statusValue =
      typeof value === 'string'
        ? value
        : (value as React.ChangeEvent<HTMLSelectElement>).target.value;

    setFilters({
      startDate,
      endDate,
      statuses: statusValue as ReturnRequestStatus,
    });
  }

  const selectedStatusesOptions = useMemo(() => {
    const statusArray = (statuses || '').split(',').filter(Boolean);

    return RETURN_REQUEST_STATUS_OPTIONS.filter(({ value }) =>
      statusArray.includes(value)
    );
  }, [statuses]);

  const dateRange = useMemo(
    () => ({ startDate, endDate }),
    [startDate, endDate]
  );

  function renderReturnRequestList() {
    if (isLoading) return <Loading />;

    if (data?.results?.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-neutral-500">Nenhuma devolução encontrada</p>
        </div>
      );
    }

    return data?.results?.map(item => (
      <ReturnRequestItem key={item.protocol} item={item} />
    ));
  }

  return (
    <Main>
      <Header />

      <div
        ref={mainRef}
        className="h-full flex flex-col gap-6 pt-8 px-4 md:px-12 pb-2 overflow-y-auto
          overflow-x-hidden"
      >
        <SearchSection />

        <ReturnRequestResume />

        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-2">
            <DateRangePicker
              onChangeRange={changeDateRange}
              onSubmit={mutate}
              onReset={resetFilters}
              values={dateRange}
              mainRef={mainRef}
              maximumDate={MAX_DATE}
              minimumDate={MIN_DATE}
              options={{ scrollIntoView: true }}
            />
            <div className="w-full md:w-60">
              <Select.Wrapper
                key="statuses"
                name="statuses"
                onChange={handleStatusChange}
                options={RETURN_REQUEST_STATUS_OPTIONS}
                initialTagged={selectedStatusesOptions}
                multiple
                searchable
              >
                <Select.Input placeholder="Selecionar Status" />
                <Select.Tags maxTags={2} />
                <Select.StatusArrow />
              </Select.Wrapper>
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full overflow-x-auto">
            {renderReturnRequestList()}
          </div>

          <Pagination
            currentPage={currentPage}
            totalCount={data?.pagination?.totalCount || 0}
            pageSize={params.pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </Main>
  );
}
